<script setup lang="ts">
import { NEmpty, NSpin } from 'naive-ui'
import QueueErrorState from './QueueErrorState.vue'
import type { AvailableModel } from '../stores/usage'
import { formatContext, formatRate, modelColor, modelName } from '../composables/usageFormatting'

// The "Available models" half of UsageView.vue (#1090) — the live
// Anthropic model catalog with pricing. Extracted so the parent stays
// under the 300-line hard limit; no behavior change from the inline
// version. Rendered independently of the weekly-spend section — its
// own load/error/empty states, its own ~24h-cached endpoint — so
// neither view can blank the other.
defineProps<{
  loading: boolean
  error: string | null
  models: AvailableModel[]
  stale: boolean
}>()

const emit = defineEmits<{ retry: [] }>()
</script>

<template>
  <section data-testid="available-models" class="flex flex-col gap-3">
    <header class="flex flex-col gap-1 border-t border-white/10 pt-6">
      <h2 class="text-sm font-medium text-white/80 m-0">Available models</h2>
      <p class="text-xs text-white/50 m-0">
        Models your account can use, pulled live from Anthropic and priced per 1M tokens. Models the brain isn’t wired
        to use yet are marked
        <span class="text-white/70">available</span> — handy for spotting newer options.
      </p>
    </header>

    <div v-if="loading" class="flex items-center justify-center py-8">
      <NSpin />
    </div>

    <QueueErrorState v-else-if="error" :message="error" @retry="emit('retry')" />

    <NEmpty v-else-if="models.length === 0" description="No models to show." class="my-8" />

    <template v-else>
      <p v-if="stale" data-testid="models-stale" class="text-xs text-amber-300/70 m-0">
        Showing last-known data — couldn’t reach Anthropic just now.
      </p>

      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <caption class="sr-only">
            Available models and per-1M-token pricing. Scroll horizontally to see all columns.
          </caption>
          <thead>
            <tr class="text-left text-xs text-white/40 border-b border-white/10">
              <th class="py-1.5 pr-3 font-medium">Model</th>
              <th class="py-1.5 px-2 font-medium">Status</th>
              <th class="py-1.5 px-2 font-medium text-right">Context</th>
              <th class="py-1.5 px-2 font-medium text-right">Input</th>
              <th class="py-1.5 px-2 font-medium text-right">Output</th>
              <th class="py-1.5 px-2 font-medium text-right hidden sm:table-cell">
                <abbr title="Cache read" class="no-underline">Cache R</abbr>
              </th>
              <th class="py-1.5 pl-2 font-medium text-right hidden sm:table-cell">
                <abbr title="Cache write" class="no-underline">Cache W</abbr>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in models" :key="m.id" data-testid="model-row" class="border-b border-white/5">
              <td class="py-2 pr-3">
                <div class="flex items-center gap-2">
                  <span class="inline-block w-2 h-2 rounded-sm shrink-0" :class="modelColor(m.id)" />
                  <div class="flex flex-col leading-tight">
                    <span class="text-white/80">{{ modelName(m) }}</span>
                    <span class="text-xs text-white/30 font-mono">{{ m.id }}</span>
                    <!-- Below sm the Cache R/W columns are hidden to keep the
                         table from overflowing; surface them here instead. -->
                    <span class="sm:hidden text-xs text-white/40 tabular-nums mt-0.5" data-testid="model-cache-mobile">
                      Cache read {{ formatRate(m.pricing?.cache_read_cents) }} · write
                      {{ formatRate(m.pricing?.cache_write_cents) }}
                    </span>
                    <span
                      v-if="m.pricing?.long_context"
                      class="text-xs text-white/40 tabular-nums mt-0.5"
                      data-testid="model-long-context"
                    >
                      Prompts over {{ formatContext(m.pricing.long_context.threshold_tokens) }}:
                      {{ formatRate(m.pricing.long_context.input_cents) }} in ·
                      {{ formatRate(m.pricing.long_context.output_cents) }} out
                    </span>
                  </div>
                </div>
              </td>
              <td class="py-2 px-2">
                <span
                  v-if="m.in_use"
                  data-testid="model-status"
                  class="inline-block rounded px-1.5 py-0.5 text-xs bg-emerald-500/15 text-emerald-300/90 whitespace-nowrap"
                  >in use · {{ m.tier }}</span
                >
                <span
                  v-else
                  data-testid="model-status"
                  class="inline-block rounded px-1.5 py-0.5 text-xs bg-white/10 text-white/50"
                  >available</span
                >
              </td>
              <td class="py-2 px-2 text-right tabular-nums text-white/60">
                {{ formatContext(m.context_window) }}
              </td>
              <td class="py-2 px-2 text-right tabular-nums text-white/70">
                {{ formatRate(m.pricing?.input_cents) }}
              </td>
              <td class="py-2 px-2 text-right tabular-nums text-white/70">
                {{ formatRate(m.pricing?.output_cents) }}
              </td>
              <td class="py-2 px-2 text-right tabular-nums text-white/50 hidden sm:table-cell">
                {{ formatRate(m.pricing?.cache_read_cents) }}
              </td>
              <td class="py-2 pl-2 text-right tabular-nums text-white/50 hidden sm:table-cell">
                {{ formatRate(m.pricing?.cache_write_cents) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="text-xs text-white/30 m-0">
        Prices are per 1M tokens. “—” means there’s no maintained price for that model yet.
      </p>
    </template>
  </section>
</template>
